import { FC, useCallback } from "react";
import { FormGroup } from "@material-ui/core";
import { includes } from "lodash";
import { usePostHog } from "posthog-js/react";

import { CheckboxField, Modal } from "components";
import ParagraphContent from "components/Modal/ParagraphContent";
import { UiParam, UiParams } from "hooks/useQueryParams/types";
import { EXCHANGE_FILTER_SORT } from "services/postHog/constants";
import theme from "theme";

import FilterSectionTitle from "./FilterSectionTitle";

interface RadioButtonFilterGroupConfig {
  title: string;
  filterOptions: UiParam[];
  key: string;
  makePostHogFilterBy: (optionName: string) => string;
}

interface RadioButtonFilterGroupModalProps {
  title: string;
  label: string[];
}

interface RadioButtonFilterGroupProps {
  filterConfig: RadioButtonFilterGroupConfig;
  uiParams: UiParams;
  setUiParams: (setState: (uiParams: UiParams) => UiParams) => void;
  modalInfo: RadioButtonFilterGroupModalProps;
}

export const RadioButtonFilterGroup: FC<RadioButtonFilterGroupProps> = ({
  filterConfig,
  uiParams,
  setUiParams,
  modalInfo,
}) => {
  const posthog = usePostHog();
  const handleFilterOption = useCallback(
    (isChecked: boolean, optionValue: string[], optionName: string) => {
      posthog?.capture(EXCHANGE_FILTER_SORT, {
        actionType: isChecked ? "unchecked" : "checked",
        filterBy: filterConfig.makePostHogFilterBy(optionName),
      });

      setUiParams(prev => ({
        ...prev,
        page: { ...prev.page, value: "1" },
        [filterConfig.key]: {
          ...prev[filterConfig.key],
          value: isChecked ? [] : optionValue,
        },
      }));
    },
    [posthog, setUiParams, filterConfig],
  );

  return (
    <>
      <FilterSectionTitle
        title={filterConfig.title}
        HelpModal={({ onClose }) => (
          <Modal open title={modalInfo.title} maxWidth="sm" onClose={onClose}>
            <ParagraphContent paragraphs={modalInfo.label} />
          </Modal>
        )}
      />
      <FormGroup>
        {filterConfig.filterOptions.map(option => {
          const isChecked =
            !!uiParams[filterConfig.key].value.length &&
            includes(uiParams[filterConfig.key].value, option.value[0]);
          return (
            <CheckboxField
              dense
              key={option.name}
              checked={isChecked}
              onChange={() =>
                handleFilterOption(
                  isChecked,
                  option.value as string[],
                  option.name,
                )
              }
              label={option.name}
              checkboxColor={theme.custom.palette.purple.main}
            />
          );
        })}
      </FormGroup>
    </>
  );
};

export default RadioButtonFilterGroup;
